import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Send,
  Search,
  MoreVertical,
  Smile,
  Paperclip,
  CheckCheck,
  Instagram,
  ImageIcon,
  Mic,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  X,
} from 'lucide-react';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Parse a date string and ensure it's treated as UTC if no timezone is specified.
 */
const parseDateAsUTC = (dateStr) => {
  if (!dateStr) return null;
  let s = dateStr;
  if (s.includes(' ') && !s.includes('T')) {
    s = s.replace(' ', 'T');
  }
  if (!s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s)) {
    s += 'Z';
  }
  return new Date(s);
};

/**
 * Format ISO datetime to a human-readable short string.
 */
const formatTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = parseDateAsUTC(isoString);
    if (!d || isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const formatMsgTime = (isoString) => {
  if (!isoString) return '';
  try {
    const d = parseDateAsUTC(isoString);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const getMessageDateLabel = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = parseDateAsUTC(dateStr);
    if (!d || isNaN(d.getTime())) return '';
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  } catch {
    return '';
  }
};

/**
 * Derive avatar initials from a username or platform_user_id.
 */
const getInitials = (contact) => {
  const name = contact?.metadata?.username || contact?.platform_user_id || '??';
  return name.slice(0, 2).toUpperCase();
};

/**
 * Pick a deterministic gradient colour for avatars.
 */
const GRADIENTS = [
  'from-pink-400 to-rose-500',
  'from-violet-400 to-purple-600',
  'from-amber-400 to-orange-500',
  'from-cyan-400 to-teal-500',
  'from-green-400 to-emerald-500',
  'from-red-400 to-pink-500',
  'from-blue-400 to-indigo-500',
  'from-yellow-400 to-amber-500',
];

const getGradient = (id) => {
  if (!id) return GRADIENTS[0];
  const hash = [...id].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return GRADIENTS[hash % GRADIENTS.length];
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const Avatar = ({ contact, size = 'md' }) => {
  const sizeClasses = { sm: 'w-8 h-8 text-xs', md: 'w-11 h-11 text-sm', lg: 'w-14 h-14 text-base' };
  const profilePic = contact?.metadata?.profile_pic;
  const gradient = getGradient(contact?.platform_user_id || contact?.conversation_id);
  const initials = getInitials(contact);

  return (
    <div
      className={`relative flex-shrink-0 rounded-full flex items-center justify-center font-bold text-white shadow-sm overflow-hidden ${sizeClasses[size]}`}
    >
      {profilePic ? (
        <img
          src={profilePic}
          alt={initials}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
            e.target.parentNode.classList.add(`bg-gradient-to-br`, ...gradient.split(' '));
          }}
        />
      ) : (
        <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${gradient}`}>
          {initials}
        </div>
      )}
    </div>
  );
};

const MessageBubble = ({ message }) => {
  const isModel = message.role === 'model' || message.role === 'human_support';
  const isMe = isModel; // "model" / "human_support" = sent by the business side

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.18 }}
      className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-1.5`}
    >
      <div
        className={`max-w-[75%] sm:max-w-[60%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed shadow-sm ${
          isMe
            ? 'bg-gradient-to-br from-gray-700 to-black text-white rounded-br-sm'
            : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm'
        }`}
      >
        {message.role === 'model' && (
          <span className="block text-[9px] font-bold uppercase tracking-widest mb-1 opacity-60">
            AI Agent
          </span>
        )}
        {message.role === 'human_support' && (
          <span className="block text-[9px] font-bold uppercase tracking-widest mb-1 opacity-60">
            Human
          </span>
        )}
        <p>{message.text}</p>
        <div className={`flex items-center gap-1.5 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
          <span className={`text-[10px] ${isMe ? 'text-gray-300' : 'text-gray-400'}`}>
            {formatMsgTime(message.date_time)}
          </span>
          {message.channel && (
            <span className={`text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${isMe ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {message.channel === 'direct_message' ? 'DM' : message.channel === 'comment' ? 'Comment' : message.channel}
            </span>
          )}
          {isMe && <CheckCheck className="h-3.5 w-3.5 text-sky-200" />}
        </div>
      </div>
    </motion.div>
  );
};

// ─── Skeletons ──────────────────────────────────────────────────────────────

const ConversationSkeleton = () => (
  <div className="w-full flex items-center gap-3 px-4 py-3 border-b border-gray-50 animate-pulse">
    <div className="w-11 h-11 rounded-full bg-gray-100 flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="flex justify-between">
        <div className="h-3 bg-gray-100 rounded w-28" />
        <div className="h-2.5 bg-gray-100 rounded w-10" />
      </div>
      <div className="h-2.5 bg-gray-100 rounded w-40" />
    </div>
  </div>
);

const MessageSkeleton = ({ align = 'left' }) => (
  <div className={`flex ${align === 'right' ? 'justify-end' : 'justify-start'} mb-1.5 animate-pulse`}>
    <div
      className={`h-9 rounded-2xl ${align === 'right' ? 'bg-gray-200 w-48' : 'bg-gray-100 w-56'}`}
    />
  </div>
);

// ─── Conversation List Item ─────────────────────────────────────────────────

const ConversationItem = ({ conversation, isActive, onClick, showNewMessageAlert, instagramAccounts }) => {
  const contact = conversation.contact || {};
  const lastMsg = Array.isArray(conversation.last_message) && conversation.last_message.length > 0
    ? conversation.last_message[0]
    : null;
  const displayName = contact?.metadata?.name || contact?.metadata?.username || contact?.platform_user_id || 'Unknown';

  // Find the associated Instagram account for the tag
  const associatedAccount = conversation.integration_id ? (instagramAccounts || []).find(acc => acc.integration_id === conversation.integration_id) : null;

  return (
    <motion.button
      onClick={onClick}
      whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
      className={`w-full flex items-center gap-3 px-4 py-3 transition-all cursor-pointer text-left border-b border-gray-50 ${
        isActive ? 'bg-gray-100 border-l-2 border-l-pink-500' : 'border-l-2 border-l-transparent'
      }`}
    >
      <Avatar contact={contact} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <div className="flex flex-col min-w-0">
            {associatedAccount && (
              <div className="flex items-center gap-1.5 mb-1 px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-bold text-black uppercase w-fit">
                <Instagram className="w-3 h-3" />
                <span>{associatedAccount.account?.instagram_username || 'IG'}</span>
              </div>
            )}
            <p className={`text-sm font-semibold truncate ${isActive ? 'text-black' : 'text-gray-900'}`}>
              {displayName}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span className="text-[10px] text-gray-400">
              {lastMsg ? formatTime(lastMsg.date_time) : ''}
            </span>
            {showNewMessageAlert && (
              <span className="w-2 h-2 bg-black rounded-full animate-pulse flex-shrink-0" title="New messages" />
            )}
          </div>
        </div>
        <p className={`text-xs truncate pr-2 ${showNewMessageAlert ? 'text-black font-semibold' : 'text-gray-400'}`}>
          {lastMsg ? lastMsg.text : 'No messages yet'}
        </p>
      </div>
    </motion.button>
  );
};

// ─── Error Banner ───────────────────────────────────────────────────────────

const ErrorBanner = ({ message, onRetry }) => (
  <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-xl mx-4 my-2">
    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
    <p className="text-xs text-red-600 font-medium flex-1">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="flex items-center gap-1 text-xs text-red-600 font-bold hover:text-red-700 transition-colors"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Retry
      </button>
    )}
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

const IntegrationInbox = ({ integrationId, integrationIds, instagramAccounts, hideContainerStyles = false }) => {
  // Normalize integrationIds to an array, and include integrationId if passed as a single prop
  const ids = Array.isArray(integrationIds) 
    ? integrationIds 
    : [integrationIds || integrationId].filter(Boolean);

  const [searchParams] = useSearchParams();
  const initialConversationId = searchParams.get('conversation_id');

  // ── Conversations state
  const [conversations, setConversations] = useState([]);
  const [convPage, setConvPage] = useState(1);
  const [convTotalPages, setConvTotalPages] = useState(1);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ── Active conversation / messages state
  const [activeConversation, setActiveConversation] = useState(null);

  // When auto-opening via URL, we start with a mocked activeConversation (no contact info).
  // Once the real conversations list loads, we replace the mock with the full object so the header shows the user's name.
  useEffect(() => {
    if (activeConversation && !activeConversation.contact && conversations.length > 0) {
      const fullConv = conversations.find(c => c.conversation_id === activeConversation.conversation_id);
      if (fullConv && fullConv.contact) {
        setActiveConversation(fullConv);
      }
    }
  }, [conversations, activeConversation]);
  const [messages, setMessages] = useState([]);
  const [msgPage, setMsgPage] = useState(1);
  const [msgTotalPages, setMsgTotalPages] = useState(1);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgLoadingMore, setMsgLoadingMore] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [convMetadata, setConvMetadata] = useState(null);
  const [showNewMessageAlert, setShowNewMessageAlert] = useState(false);

  // ── Send state
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);

  // ── Mobile navigation
  const [showChat, setShowChat] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Close emoji picker on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const COMMON_EMOJIS = [
    '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃',
    '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜',
    '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟',
    '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠',
    '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗',
    '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
    '👍', '👎', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✌️', '🤞', '🤟', '🤘',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕',
    '🔥', '✨', '🌟', '💫', '💥', '💯', '💢', '💨', '💦', '💤', '🎉', '🎊'
  ];

  const handleEmojiClick = (emoji) => {
    setInputValue(prev => prev + emoji);
  };
  const prevScrollHeightRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [activeConversation]);

  // ────────────────────────────────────────────────────────────────────────
  // Auto-resize textarea with animation
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    // Step 1: disable transition and reset to 'auto' so browser can measure true height
    el.style.transition = 'none';
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 144); // cap at 6 lines (~144px)
    // Step 2: on next frame, re-enable transition and apply the measured height
    requestAnimationFrame(() => {
      el.style.transition = 'height 0.18s ease';
      el.style.height = newHeight + 'px';
    });
  }, [inputValue]);

  // ────────────────────────────────────────────────────────────────────────
  // Fetch conversations
  // ────────────────────────────────────────────────────────────────────────
  const fetchConversations = useCallback(
    async (page = 1, search = searchQuery, append = false, isSilent = false) => {
      if (!ids.length) return;
      try {
        if (!isSilent) {
          setConvLoading(true);
          setConvError(null);
        }

        // Fetch conversations from all integration IDs in parallel
        const fetchPromises = ids.map(id => 
          ApiService.getInstagramConversations(id, { page, perPage: 15, search })
            .then(res => ({ id, res }))
            .catch(err => ({ id, error: err }))
        );

        const results = await Promise.all(fetchPromises);
        
        let allIncoming = [];
        let maxTotalPages = 1;

        results.forEach(({ id, res, error }) => {
          if (res?.success) {
            const incoming = (res.data || []).map(conv => ({ ...conv, integration_id: id }));
            allIncoming = [...allIncoming, ...incoming];
            maxTotalPages = Math.max(maxTotalPages, res.pagination?.total_pages || 1);
          } else if (error && !isSilent) {
            console.error(`Error fetching for integration ${id}:`, error);
          }
        });

        // Sort merged conversations by last message date
        allIncoming.sort((a, b) => {
          const timeA = a.last_message?.[0]?.date_time ? new Date(a.last_message[0].date_time).getTime() : 0;
          const timeB = b.last_message?.[0]?.date_time ? new Date(b.last_message[0].date_time).getTime() : 0;
          return timeB - timeA;
        });

        setConversations((prev) => (append ? [...prev, ...allIncoming] : allIncoming));
        setConvPage(page);
        setConvTotalPages(maxTotalPages);
      } catch (err) {
        if (!isSilent) {
          const msg = err.response?.data?.message || err.message || 'Failed to load conversations';
          setConvError(msg);
          if (!append) setConversations([]);
        }
      } finally {
        if (!isSilent) {
          setConvLoading(false);
        }
      }
    },
    [ids.join(','), searchQuery]
  );

  // ────────────────────────────────────────────────────────────────────────
  // Search with debounce
  // ────────────────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(val);
      fetchConversations(1, val, false);
    }, 400);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    fetchConversations(1, '', false);
  };

  // ────────────────────────────────────────────────────────────────────────
  // Load more conversations
  // ────────────────────────────────────────────────────────────────────────
  const loadMoreConversations = () => {
    if (convLoading || convPage >= convTotalPages) return;
    fetchConversations(convPage + 1, searchQuery, true);
  };

  // ────────────────────────────────────────────────────────────────────────
  // Fetch messages for a conversation
  // ────────────────────────────────────────────────────────────────────────
  const fetchMessages = useCallback(
    async (conversation, page = 1, append = false, isSilent = false) => {
      if (!conversation || !conversation.integration_id) return;
      try {
        if (!isSilent) {
          if (append) setMsgLoadingMore(true);
          else setMsgLoading(true);
          setMsgError(null);
        }

        const data = await ApiService.getInstagramMessages(
          conversation.integration_id,
          conversation.conversation_id,
          { page, perPage: 30 }
        );
        if (data?.success) {
          const incoming = data.data || [];
          // API returns newest-first per page, so we prepend when loading older pages
          setMessages((prev) => {
            if (append) {
              return [...incoming, ...prev];
            } else if (isSilent) {
              const filteredPrev = prev.filter((m) => !m._optimistic);
              const prevIds = new Set(filteredPrev.map((m) => m._id).filter(Boolean));
              const newMessages = incoming.filter((m) => {
                if (m._id && prevIds.has(m._id)) return false;
                const hasMatch = filteredPrev.some(
                  (pm) =>
                    pm.text === m.text &&
                    Math.abs(new Date(pm.date_time) - new Date(m.date_time)) < 45000
                );
                if (hasMatch) return false;
                return true;
              });
              return [...filteredPrev, ...newMessages];
            } else {
              return incoming;
            }
          });
          if (data.conversation_metadata) {
            setConvMetadata(data.conversation_metadata);
          }
          if (!isSilent) {
            setMsgPage(data.pagination?.page || page);
            setMsgTotalPages(data.pagination?.total_pages || 1);
          }
        } else {
          if (!isSilent) {
            throw new Error(data?.message || 'Failed to load messages');
          }
        }
      } catch (err) {
        if (!isSilent) {
          const msg = err.response?.data?.message || err.message || 'Failed to load messages';
          setMsgError(msg);
          if (!append) setMessages([]);
        }
      } finally {
        if (!isSilent) {
          setMsgLoading(false);
          setMsgLoadingMore(false);
        }
      }
    },
    [integrationId]
  );

  // ─── Polling for background conversations refresh ───────────────────────────
  useEffect(() => {
    if (!ids.length) return;

    const interval = setInterval(() => {
      // Refresh conversations silently
      fetchConversations(1, searchQuery, false, true);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [ids.join(','), searchQuery, fetchConversations]);

  // ─── Polling for background messages refresh ───────────────────────────────
  useEffect(() => {
    if (!activeConversation || sending) return;

    const interval = setInterval(() => {
      // Refresh messages silently
      fetchMessages(activeConversation, 1, false, true);
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [activeConversation, sending, fetchMessages]);

  // ────────────────────────────────────────────────────────────────────────
  // Select a conversation
  // ────────────────────────────────────────────────────────────────────────
  const handleSelectConversation = (conv) => {
    if (activeConversation?.conversation_id === conv.conversation_id) return;
    setActiveConversation(conv);
    setMessages([]);
    setConvMetadata(null);
    setMsgPage(1);
    setMsgTotalPages(1);
    setMsgError(null);
    setShowChat(true);
    setShowNewMessageAlert(false);
    fetchMessages(conv, 1, false);
  };

  // Initial load — intentionally only re-runs when integrationIds change
  useEffect(() => {
    fetchConversations(1, '', false);
    
    // Auto-open specific conversation if passed via URL
    if (initialConversationId && ids.length > 0) {
      handleSelectConversation({
        conversation_id: initialConversationId,
        integration_id: ids[0]
      });
    }
  }, [ids.join(',')]); // eslint-disable-line

  // ────────────────────────────────────────────────────────────────────────
  // Infinite Scroll Handlers
  // ────────────────────────────────────────────────────────────────────────
  const handleConversationScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (!convLoading && convPage < convTotalPages) {
        loadMoreConversations();
      }
    }
  };

  const handleMessageScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop < 50) {
      if (!msgLoading && !msgLoadingMore && msgPage < msgTotalPages) {
        prevScrollHeightRef.current = scrollHeight;
        loadOlderMessages();
      }
    }

    const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
    if (isNearBottom) {
      setShowNewMessageAlert(false);
    }
  };

  // Adjust scroll position after loading older messages
  useEffect(() => {
    if (!msgLoadingMore && prevScrollHeightRef.current > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const newScrollHeight = container.scrollHeight;
      container.scrollTop = container.scrollTop + (newScrollHeight - prevScrollHeightRef.current);
      prevScrollHeightRef.current = 0;
    }
  }, [messages.length, msgLoadingMore]);

  // ────────────────────────────────────────────────────────────────────────
  // Scroll to bottom when messages update
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;

    // Only scroll to bottom if we are not loading older messages
    if (messagesEndRef.current && !msgLoadingMore && prevScrollHeightRef.current === 0) {
      // Scroll to bottom on initial load, or if user is near bottom
      if (msgLoading || isNearBottom || isInitialLoadRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: isInitialLoadRef.current ? 'auto' : 'smooth' });
        if (!msgLoading && messages.length > 0) {
          isInitialLoadRef.current = false;
        }
      }
    }
  }, [messages.length, msgLoading, msgLoadingMore]);

  // ── Show new message alert/notification when scrolled up
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0 || msgLoading || msgLoadingMore) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage._optimistic) return; // ignore optimistic messages we just sent
    const isMe = lastMessage.role === 'model' || lastMessage.role === 'human_support';

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;

    if (!isNearBottom && !isMe) {
      setShowNewMessageAlert(true);
    }
  }, [messages.length, msgLoading, msgLoadingMore]);

  // ────────────────────────────────────────────────────────────────────────
  // Load older messages (pagination)
  // ────────────────────────────────────────────────────────────────────────
  const loadOlderMessages = () => {
    if (msgLoadingMore || msgLoading || msgPage >= msgTotalPages) return;
    fetchMessages(activeConversation, msgPage + 1, true);
  };

  // ────────────────────────────────────────────────────────────────────────
  // Send a DM
  // ────────────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!inputValue.trim() || !activeConversation || sending || !activeConversation.integration_id) return;

    const text = inputValue.trim();
    const recipientId = activeConversation.contact?.platform_user_id;

    if (!recipientId) {
      toast.error('Could not determine recipient ID.');
      return;
    }

    // Optimistic message
    const optimistic = {
      _optimistic: true,
      role: 'human_support',
      text,
      date_time: new Date().toISOString(),
      channel: 'direct_message',
    };
    setMessages((prev) => [...prev, optimistic]);
    setInputValue('');
    inputRef.current?.focus();

    try {
      setSending(true);
      await ApiService.sendInstagramDM(activeConversation.integration_id, {
        conversation_id: activeConversation.conversation_id,
        message_text: text,
      });
      toast.success('Message sent!');
      // Refresh messages to get the confirmed server-side copy silently
      await fetchMessages(activeConversation, 1, false, true);
    } catch (err) {
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m !== optimistic));
      setInputValue(text); // restore input
      const msg = err.response?.data?.message || err.message || 'Failed to send message';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBack = () => {
    setShowChat(false);
    setActiveConversation(null);
    setMessages([]);
  };

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────
  const activeContact = activeConversation?.contact || convMetadata?.contact || convMetadata || {};
  const activeDisplayName = activeContact?.metadata?.name || activeContact?.metadata?.username || activeContact?.platform_user_id || activeContact?.name || activeContact?.username || 'Unknown';

  // Check if the current conversation's account is disconnected
  // Handle both integration_id and id for flexibility
  const associatedAccount = activeConversation?.integration_id 
    ? (instagramAccounts || []).find(acc => (acc.integration_id || acc.id) === activeConversation.integration_id) 
    : null;
  const isDisconnected = associatedAccount?.overall_status === 'disconnected' || 
                         associatedAccount?.overall_status === 'token_expired' ||
                         associatedAccount?.status === 'disconnected' ||
                         associatedAccount?.status === 'token_expired';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={hideContainerStyles ? "flex flex-1 overflow-hidden bg-white h-full" : "flex rounded-3xl overflow-hidden border border-gray-200 shadow-sm bg-white"}
      style={hideContainerStyles ? {} : { height: 'calc(100vh - 280px)', minHeight: '520px' }}
    >

      {/* ── Left Sidebar: Conversation List ─────────────────────── */}
      <div
        className={`flex flex-col border-r border-gray-100 bg-white ${
          showChat ? 'hidden md:flex' : 'flex'
        } w-full md:w-80 lg:w-96 flex-shrink-0`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center">
              <Instagram className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-black text-gray-900">Instagram Inbox</h2>
              <p className="text-[10px] text-gray-400 font-medium">
                {convLoading && conversations.length === 0
                  ? 'Loading…'
                  : `${conversations.length} conversations`}
              </p>
            </div>
          </div>
          <button
            onClick={() => fetchConversations(1, searchQuery, false)}
            disabled={convLoading}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400 hover:text-gray-600 disabled:opacity-40"
            title="Refresh conversations"
          >
            <RefreshCw className={`h-4 w-4 ${convLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-gray-50">
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
            <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search conversations…"
              value={searchInput}
              onChange={handleSearchChange}
              className="flex-1 bg-transparent text-xs text-gray-700 placeholder-gray-400 outline-none font-medium"
            />
            {searchInput && (
              <button onClick={clearSearch} className="text-gray-400 hover:text-gray-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {convError && (
          <ErrorBanner
            message={convError}
            onRetry={() => fetchConversations(1, searchQuery, false)}
          />
        )}

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto" onScroll={handleConversationScroll}>
          {convLoading && conversations.length === 0 ? (
            <>
              {[...Array(6)].map((_, i) => (
                <ConversationSkeleton key={i} />
              ))}
            </>
          ) : conversations.length === 0 && !convError ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 py-10">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
                <Instagram className="h-7 w-7 text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-400">No conversations found</p>
              <p className="text-xs text-gray-300 mt-1">
                {searchInput ? 'Try a different search term' : 'Conversations will appear here when followers DM you'}
              </p>
            </div>
          ) : (
            <>
              {conversations.map((conv) => (
                <ConversationItem
                  key={conv.conversation_id}
                  conversation={conv}
                  isActive={activeConversation?.conversation_id === conv.conversation_id}
                  showNewMessageAlert={showNewMessageAlert && activeConversation?.conversation_id === conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  instagramAccounts={instagramAccounts}
                />
              ))}

              {/* Infinite scroll loading indicator */}
              {convLoading && conversations.length > 0 && (
                <div className="w-full flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 text-black animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Right: Chat Area ──────────────────────────────────────── */}
      <div
        className={`flex-1 flex flex-col bg-gray-50/50 ${showChat ? 'flex' : 'hidden md:flex'}`}
      >
        <AnimatePresence mode="wait">
          {activeConversation ? (
            <motion.div
              key={activeConversation.conversation_id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col h-full"
            >
              {/* Chat Header */}
              <div className="flex items-center justify-between px-4 py-3.5 bg-white border-b border-gray-100 shadow-sm flex-shrink-0">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleBack}
                    className="md:hidden p-1.5 -ml-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>

                  <Avatar contact={activeContact} size="md" />

                  <div>
                    <p className="text-sm font-bold text-gray-900">{activeDisplayName}</p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-medium text-gray-400">
                        {activeContact?.platform_user_id || ''}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fetchMessages(activeConversation, 1, false)}
                    disabled={msgLoading}
                    className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-colors disabled:opacity-40"
                    title="Refresh messages"
                  >
                    <RefreshCw className={`h-4 w-4 ${msgLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <button className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-colors">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              <div className="relative flex-1 min-h-0 flex flex-col">
                <div 
                  ref={messagesContainerRef}
                  onScroll={handleMessageScroll}
                  className="flex-1 overflow-y-auto px-4 py-4 space-y-6"
                >
                  {/* Infinite scroll loading indicator */}
                  {msgLoadingMore && (
                    <div className="flex justify-center pb-4">
                      <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                    </div>
                  )}

                  {/* Message loading skeletons */}
                  {msgLoading && (
                    <div className="space-y-1.5">
                      <MessageSkeleton align="left" />
                      <MessageSkeleton align="right" />
                      <MessageSkeleton align="left" />
                      <MessageSkeleton align="right" />
                      <MessageSkeleton align="left" />
                    </div>
                  )}

                  {/* Message error */}
                  {msgError && !msgLoading && (
                    <ErrorBanner
                      message={msgError}
                      onRetry={() => fetchMessages(activeConversation, 1, false)}
                    />
                  )}

                  {/* Messages */}
                  {!msgLoading && messages.length === 0 && !msgError && (
                    <div className="flex flex-col items-center justify-center h-40 text-center">
                      <p className="text-sm font-semibold text-gray-400">No messages yet</p>
                      <p className="text-xs text-gray-300 mt-1">Start the conversation below</p>
                    </div>
                  )}

                  {!msgLoading && (() => {
                    // Group messages by date label
                    const groups = [];
                    let currentGroup = null;

                    messages.forEach((msg, idx) => {
                      const dateLabel = getMessageDateLabel(msg.date_time) || 'Unknown';
                      if (!currentGroup || currentGroup.dateLabel !== dateLabel) {
                        currentGroup = {
                          dateLabel,
                          messages: []
                        };
                        groups.push(currentGroup);
                      }
                      currentGroup.messages.push({ msg, idx });
                    });

                    return groups.map((group) => (
                      <div key={group.dateLabel} className="relative">
                        {/* Sticky Date Separator */}
                        <div className="sticky top-2 z-10 flex justify-center mb-4 pointer-events-none">
                          <span className="bg-gray-200/90 backdrop-blur-sm text-gray-800 text-[10px] font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wider pointer-events-auto">
                            {group.dateLabel}
                          </span>
                        </div>
                        {/* Messages for this date group */}
                        <div className="space-y-3 flex flex-col">
                          {group.messages.map(({ msg, idx }) => (
                            <MessageBubble key={msg._id || `${msg.date_time}-${idx}`} message={msg} />
                          ))}
                        </div>
                      </div>
                    ));
                  })()}

                  <div ref={messagesEndRef} />
                </div>

                {/* Floating New Messages Pill */}
                <AnimatePresence>
                  {showNewMessageAlert && (
                    <motion.button
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.9 }}
                      onClick={() => {
                        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        setShowNewMessageAlert(false);
                      }}
                      className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-full shadow-lg text-xs font-bold flex items-center gap-1.5 transition-all z-20 cursor-pointer"
                    >
                      <ChevronDown className="h-4 w-4 animate-bounce" />
                      New Messages
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Input Area */}
              <div className="px-4 py-3.5 bg-white border-t border-gray-100 flex-shrink-0">
                {isDisconnected ? (
                  <div className="flex items-center gap-3 px-4 py-3.5 bg-red-50 border border-red-100 rounded-2xl text-red-800 justify-center">
                    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span className="text-xs font-bold text-center">
                      Your account is disconnected or token expired, please reconnect again.
                    </span>
                  </div>
                ) : convMetadata && convMetadata.can_reply === false ? (
                  <div className="flex items-center gap-3 px-4 py-3.5 bg-amber-50 border border-amber-100 rounded-2xl text-amber-800 justify-center">
                    <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <span className="text-xs font-bold text-center">
                      It's been 24 hours the user messaged you now you can not reply
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end gap-2.5">
                      {/*
                      <button disabled className="p-2 text-gray-400 rounded-xl transition-colors flex-shrink-0 hidden sm:flex opacity-50 cursor-not-allowed">
                        <Paperclip className="h-5 w-5" />
                      </button>
                      <button disabled className="p-2 text-gray-400 rounded-xl transition-colors flex-shrink-0 hidden sm:flex opacity-50 cursor-not-allowed">
                        <ImageIcon className="h-5 w-5" />
                      </button>
                      */}

                      <div className="flex-1 flex items-end bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 gap-2 focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-pink-100 transition-all">
                        <textarea
                          ref={inputRef}
                          rows={1}
                          value={inputValue}
                          onChange={(e) => {
                            setInputValue(e.target.value);
                          }}
                          onKeyDown={handleKeyDown}
                          placeholder="Type a message…"
                          maxLength={1000}
                          className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none resize-none leading-relaxed overflow-y-hidden font-medium"
                          style={{ minHeight: '24px', maxHeight: '144px' }}
                          disabled={sending}
                        />
                        <div className="relative" ref={emojiPickerRef}>
                          <button 
                            type="button"
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className={`hover:text-black transition-colors flex-shrink-0 ${showEmojiPicker ? 'text-black' : 'text-gray-400'}`}
                          >
                            <Smile className="h-5 w-5" />
                          </button>

                          {/* Emoji Picker Popover */}
                          <AnimatePresence>
                            {showEmojiPicker && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                                className="absolute bottom-full right-0 mb-3 bg-white border border-gray-100 shadow-xl rounded-2xl p-3 w-64 z-50"
                              >
                                <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                                  {COMMON_EMOJIS.map((emoji, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => handleEmojiClick(emoji)}
                                      className="text-xl hover:bg-gray-100 rounded-lg p-1 transition-colors flex items-center justify-center cursor-pointer"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                                {/* Little triangle arrow */}
                                <div className="absolute -bottom-2 right-1.5 w-4 h-4 bg-white border-b border-r border-gray-100 transform rotate-45" />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {inputValue.trim() ? (
                        <motion.button
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.8, opacity: 0 }}
                          onClick={handleSend}
                          disabled={sending}
                          className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-gray-700 to-black hover:from-pink-600 hover:to-rose-600 text-white rounded-2xl flex items-center justify-center shadow-md shadow-gray-200 transition-all active:scale-95 disabled:opacity-50"
                        >
                          {sending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </motion.button>
                      ) : (
                        <motion.button
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          disabled
                          className="flex-shrink-0 w-10 h-10 bg-gray-100 text-gray-300 rounded-2xl flex items-center justify-center transition-all opacity-50 cursor-not-allowed"
                        >
                          <Send className="h-4 w-4" />
                        </motion.button>
                      )}
                    </div>

                    <p className="text-[10px] text-gray-300 text-center mt-2 font-medium">
                      Press Enter to send &middot; Shift+Enter for new line
                    </p>
                  </>
                )}
              </div>
            </motion.div>
          ) : (
            /* Empty state */
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex-1 flex flex-col items-center justify-center text-center p-8"
            >
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-tr from-gray-700 via-gray-900 to-black p-0.5 shadow-xl mb-5 flex items-center justify-center">
                <div className="w-full h-full bg-white rounded-[22px] flex items-center justify-center">
                  <Instagram className="h-10 w-10 text-black" />
                </div>
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Instagram Inbox</h3>
              <p className="text-sm text-gray-400 font-medium max-w-xs leading-relaxed">
                Select a conversation from the left to start chatting with your Instagram followers.
              </p>
              {!integrationId && (
                <div className="mt-5 flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-2xl">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="text-xs font-bold text-amber-600">
                    No integration ID found. Please reconnect Instagram.
                  </span>
                </div>
              )}
              {integrationId && conversations.length > 0 && (
                <div className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-2xl">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-xs font-bold text-black">
                    {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} loaded
                  </span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default IntegrationInbox;
